import {
  AuthenticationData,
  IAuthService,
  LoginResponse,
  TwoFactorConfirmResponse,
  TwoFactorSetupResponse,
  TwoFactorRequiredResponse,
} from 'src/types/AuthService';
import Cookies from 'js-cookie';
import { axiosService } from './AxiosService';
import { IUser } from 'src/types/User';
import { AxiosError } from 'axios';

const AUTHENTICATION_USER = 'AUTHENTICATION_USER';
const AUTHENTICATION_TOKEN = 'AUTHENTICATION_TOKEN';

const transformUserResponse = ({
  id,
  first_name,
  last_name,
  clinic_id,
  email,
}: Record<string, unknown>): IUser => {
  return {
    id: parseInt(id as string, 10),
    firstName: first_name as string,
    lastName: last_name as string,
    clinicId: parseInt(clinic_id as string, 10),
    email: email as string,
  };
};

export const login = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const { headers, data, status } = await axiosService
      .getPublicInstance()
      .post('/login', {
        user: {
          email,
          password,
        },
      });

    // Check if 2FA is required (status 200 with requires_otp)
    const responseData = data as Record<string, unknown>;
    if (status === 200 && responseData.requires_otp === true) {
      return {
        requires_otp: true,
        pending_2fa_token: responseData.pending_2fa_token as string,
        message: responseData.message as string,
      } as TwoFactorRequiredResponse;
    }

    // Normal login flow (status 201 with token in header)
    if (
      !headers.authorization ||
      headers.authorization.split(' ').length <= 1
    ) {
      throw new Error('Could not login');
    }

    const authorizationParts = headers.authorization.split(' ');
    const authenticationToken = authorizationParts[1];

    // The user data is nested in responseData.user, not at the root level
    const transformedUser = transformUserResponse(
      responseData.user as Record<string, unknown>
    );

    saveAuthenticationData(transformedUser, authenticationToken);

    return {
      authenticationToken,
      user: transformedUser,
    };
  } catch (error) {
    // If it's a server error such as 401 or 422, try to get the message
    if ((error as AxiosError)?.response?.data) {
      const responseData = (error as AxiosError)?.response?.data;

      throw new Error((responseData as Record<string, string>).error);
    }

    throw error;
  }
};

export const logout = async (): Promise<void> => {
  Cookies.remove(AUTHENTICATION_TOKEN);
  Cookies.remove(AUTHENTICATION_USER);

  return Promise.resolve();
};

export const getAuthenticationData = (): AuthenticationData | null => {
  const authenticationToken = Cookies.get(AUTHENTICATION_TOKEN);
  const cookiesUser = Cookies.get(AUTHENTICATION_USER);

  if (!authenticationToken || !cookiesUser) {
    return null;
  }

  try {
    // Parse the user directly - it's already stored in the correct IUser format
    const user = JSON.parse(cookiesUser) as IUser;

    // Validate that the user object has required fields
    if (!user || !user.id || !user.email) {
      throw new Error('Invalid user data in cookies');
    }

    return { authenticationToken, user };
  } catch (error) {
    // If we can't parse the user data, clear the cookies and return null
    Cookies.remove(AUTHENTICATION_TOKEN);
    Cookies.remove(AUTHENTICATION_USER);
    return null;
  }
};

/**
 * Save authentication data to cookies
 */
export const saveAuthenticationData = (
  user: IUser,
  authenticationToken: string
): void => {
  const userString = JSON.stringify(user);
  Cookies.set(AUTHENTICATION_USER, userString);
  Cookies.set(AUTHENTICATION_TOKEN, authenticationToken);
};

/**
 * Setup 2FA - Generate QR code and secret
 */
export const setupTwoFactor = async (token?: string): Promise<TwoFactorSetupResponse> => {
  try {
    const { data } = await axiosService
      .getSecuredInstance(token)
      .post('/two_factor/setup', {});

    return data as TwoFactorSetupResponse;
  } catch (error) {
    if ((error as AxiosError)?.response?.data) {
      const responseData = (error as AxiosError)?.response?.data;
      throw new Error((responseData as Record<string, string>).error);
    }
    throw error;
  }
};

/**
 * Confirm 2FA setup - Enable 2FA with OTP code
 */
export const confirmTwoFactor = async (
  otpCode: string,
  token?: string
): Promise<TwoFactorConfirmResponse> => {
  try {
    const { data } = await axiosService
      .getSecuredInstance(token)
      .post('/two_factor/confirm', {
        otp_code: otpCode,
      });

    return data as TwoFactorConfirmResponse;
  } catch (error) {
    if ((error as AxiosError)?.response?.data) {
      const responseData = (error as AxiosError)?.response?.data;
      throw new Error((responseData as Record<string, string>).error);
    }
    throw error;
  }
};

/**
 * Verify OTP during login - Complete 2FA verification
 */
export const verifyOTP = async (
  otpCode: string,
  pending2faToken: string
): Promise<AuthenticationData> => {
  try {
    const { data, headers } = await axiosService
      .getPublicInstance()
      .post('/two_factor/verify', {
        otp_code: otpCode,
        pending_2fa_token: pending2faToken,
      });

    // Token is now in Authorization header, not response body
    if (!headers.authorization || headers.authorization.split(' ').length <= 1) {
      throw new Error('Could not verify OTP - no authorization token');
    }

    const authorizationParts = headers.authorization.split(' ');
    const authenticationToken = authorizationParts[1];

    const responseData = data as Record<string, unknown>;
    const transformedUser = transformUserResponse(
      responseData.user as Record<string, unknown>
    );

    saveAuthenticationData(transformedUser, authenticationToken);

    return {
      authenticationToken,
      user: transformedUser,
    };
  } catch (error) {
    if ((error as AxiosError)?.response?.data) {
      const responseData = (error as AxiosError)?.response?.data;
      throw new Error((responseData as Record<string, string>).error);
    }
    throw error;
  }
};

export const authService: IAuthService = Object.freeze({
  login,
  logout,
  getAuthenticationData,
  setupTwoFactor,
  confirmTwoFactor,
  verifyOTP,
});
